import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Icon,
  MaxWidthText,
  Pagination,
  Table,
  Tooltip,
} from '@makeably/creativex-design-system';
import {
  MAX_PER_PAGE,
  objectsPerPage,
  PER_PAGE_DEFAULT,
} from 'utilities/array';
import {
  getItemContent,
  itemProps,
} from 'utilities/item';
import { sanitizeString } from 'utilities/string';
import styles from 'components/molecules/GroupedHeaderItemsTable.module.css';

export const headerProps = PropTypes.shape({
  element: PropTypes.element,
  group: PropTypes.string,
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortable: PropTypes.bool,
});
const propTypes = {
  className: PropTypes.string,
  emptyTableContent: PropTypes.node,
  groupTooltips: PropTypes.objectOf(PropTypes.string),
  headers: PropTypes.arrayOf(headerProps).isRequired,
  items: PropTypes.arrayOf(itemProps).isRequired,
  maxWidthSize: PropTypes.string,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  page: PropTypes.number,
  perPage: PropTypes.number,
  sort: PropTypes.shape({
    asc: PropTypes.bool,
    key: PropTypes.string,
  }),
};
const defaultProps = {
  className: undefined,
  emptyTableContent: null,
  groupTooltips: {},
  maxWidthSize: 'medium',
  onPageChange: undefined,
  onSortChange: undefined,
  page: 1,
  perPage: undefined,
  sort: undefined,
};

function getHeaderIcon(key, sort) {
  if (key !== sort?.key) {
    return 'blank';
  }
  return sort?.asc ? 'smallArrowUp' : 'smallArrowDown';
}

function getSortLabel(header, sort) {
  if (sort && header.key === sort.key) {
    if (sort.asc) {
      return 'ascending';
    }
    return 'descending';
  }
  return 'none';
}

function renderHeaders(headers, sort, onClick, tooltipLookup) {
  return headers.reduce((all, header) => {
    const [groupedHeaders, subHeaders] = all;

    const lastIndex = groupedHeaders.length - 1;
    const lastGroup = groupedHeaders[lastIndex];
    const group = header.group || '';
    let newGroupedHeaders;

    if (lastGroup && lastGroup.group === group) {
      newGroupedHeaders = [
        ...groupedHeaders.slice(0, lastIndex),
        {
          ...lastGroup,
          colSpan: lastGroup.colSpan + 1,
        },
      ];
    } else {
      const groupLabel = group.replaceAll('\n', ' ');
      const tooltip = tooltipLookup[group];
      let label = groupLabel;

      if (tooltip) {
        label = <Tooltip label={tooltip}>{ groupLabel }</Tooltip>;
      }

      newGroupedHeaders = [
        ...groupedHeaders,
        {
          colSpan: 1,
          group,
          value: <div key={group} className="t-body-1">{ label }</div>,
        },
      ];
    }

    const isSortable = header.sortable ?? true;
    const handleClick = isSortable ? () => onClick(header.key) : null;
    const buttonClasses = classNames(
      styles.headerButton,
      {
        [styles.clickable]: isSortable,
      },
    );
    const sortLabel = getSortLabel(header, sort);

    let element;
    if (handleClick) {
      element = (
        <button
          key={header.key}
          className={buttonClasses}
          type="button"
          onClick={handleClick}
        >
          { header.element ?? header.label }
          <Icon name={getHeaderIcon(header.key, sort)} />
        </button>
      );
    } else {
      element = (
        <div
          key={header.key}
          className={buttonClasses}
        >
          { header.element ?? header.label }
          <Icon name={getHeaderIcon(header.key, sort)} />
        </div>
      );
    }

    return [
      newGroupedHeaders,
      [
        ...subHeaders,
        {
          sortLabel,
          value: element,
        },
      ],
    ];
  }, [[], []]);
}

function renderCell(item, key, maxWidthSize) {
  const content = getItemContent(item, key);
  // Sanitize the ID for use as a class
  const sanitizedItemId = sanitizeString(item.id.value.toString());
  const sanitizedKey = sanitizeString(key);
  const cellClasses = `tableRow-${sanitizedItemId} tableColumn-${sanitizedKey}`;

  switch (typeof content) {
    case 'string':
    case 'number':
      return {
        className: cellClasses,
        value: (
          <div aria-label={content}>
            <MaxWidthText
              className={styles.cell}
              size={maxWidthSize}
              text={`${content}`}
            />
          </div>
        ),
      };
    default:
      return {
        className: cellClasses,
        value: content,
      };
  }
}

function renderRow(item, keys, maxWidthSize) {
  return {
    cells: keys.map((key) => renderCell(item, key, maxWidthSize)),
    key: `${item.id.value}`,
  };
}

function renderPagination(total, currentPage, perPage, onPageChange) {
  if (total <= perPage) return null;

  return (
    <div className={styles.pagination}>
      <Pagination
        currentPage={currentPage}
        perPage={perPage}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function GroupedHeaderItemsTable({
  className,
  emptyTableContent,
  groupTooltips,
  headers,
  items,
  maxWidthSize,
  onPageChange,
  onSortChange,
  page,
  perPage,
  sort,
}) {
  if (items.length === 0) return emptyTableContent;

  const onHeaderClick = (key) => {
    onPageChange?.(1);

    // @note: the parent component is responsible for actually sorting the items
    if (key === sort?.key) {
      onSortChange?.({
        asc: !(sort?.asc),
        key,
      });
    } else {
      const asc = true;
      onSortChange?.({
        asc,
        key,
      });
    }
  };

  const itemsPerPage = onPageChange ? (perPage ?? PER_PAGE_DEFAULT) : MAX_PER_PAGE;
  const pageItems = objectsPerPage(items, page, itemsPerPage, onPageChange);

  const keys = headers.map(({ key }) => key);
  const rows = pageItems.map((item) => renderRow(item, keys, maxWidthSize));

  return (
    <div className={`u-scrollShadowRight ${styles.table}`}>
      <Table
        className={className}
        headers={renderHeaders(headers, sort, onHeaderClick, groupTooltips)}
        rows={rows}
      />
      { renderPagination(items.length, page, itemsPerPage, onPageChange) }
    </div>
  );
}

GroupedHeaderItemsTable.propTypes = propTypes;
GroupedHeaderItemsTable.defaultProps = defaultProps;

export default GroupedHeaderItemsTable;
